import { Deliverable } from '@wpmedia/swordfish-ts/dist/types/Swordfish';
import { TranslationKey, TranslationReplacements, translate } from './i18n';
import { PlayerUiOptions } from './player-ui-options';
import { CaptionTrackData, KoiConfig, MediaData, MediaType, VideoData } from './types';

const defaultSampleRate = 0.001;

type LoadingStateOptions = {
  minHeight?: number;
  aspectRatio?: string;
};

// I clone this template into the koi's shadow DOM, to avoid parsing costs with `innerHTML`.
const styleTemplate = document.createElement('template');
styleTemplate.innerHTML = `
    <style>
      :host {
        display: block;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
      }
      media-controller {
        width: 100%;
        border-radius: var(--border-radius, 12px);
        overflow: hidden;
        background-clip: padding-box;
        background-color: rgba(255, 255, 255, 1);;
      }
      media-controller[audio] {
        width: auto;
        min-width: 300px;
        max-width: 100%;
      }
      .koi-loading-state {
        width: 100%;
        min-height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 40px 24px;
        border-radius: var(--border-radius, 12px);
        background: var(--koi-loading-background, #bec2d9);
        color: var(--koi-loading-color, #16162a);
        text-align: center;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
        transition: min-height 0.2s ease;
      }
      .koi-loading-row {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        font-size: 1.05rem;
        font-weight: 500;
      }
      .koi-loading-spinner {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        border: 3px solid var(--koi-loading-track-color, rgba(22, 22, 42, 0.15));
        border-top-color: var(--koi-loading-indicator-color, #16162a);
        animation: koi-loading-spin 1s linear infinite;
      }
      .koi-loading-label {
        font-size: 1rem;
        letter-spacing: 0.01em;
      }
      @keyframes koi-loading-spin {
        to {
          transform: rotate(360deg);
        }
      }
    </style>
    `;

class BasePlayer extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['class'];
  }

  shadowRoot!: ShadowRoot;
  hideOptions: string[];
  orgId?: string;
  env?: string;
  mediaId?: string;
  stream?: string;
  type?: MediaType;
  meta: {
    ready: boolean;
    title?: string;
    sampleRate?: number;
    live?: boolean;
    captions?: boolean;
  };
  showWaveform?: boolean;
  arcToken?: string;
  private _registeredMediaController?: HTMLElement;
  protected loadingContainer?: HTMLDivElement;
  protected frameStyleOptions?: LoadingStateOptions;
  private getLocale(): string | undefined {
    const attrLang = this.getAttribute('lang');
    if (attrLang) {
      return attrLang;
    }

    const docLocale = this.ownerDocument?.documentElement?.getAttribute('lang');
    if (docLocale) {
      return docLocale;
    }

    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }

    return undefined;
  }

  protected t(key: TranslationKey, replacements?: TranslationReplacements): string {
    return translate(key, { locale: this.getLocale(), replacements });
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(styleTemplate.content.cloneNode(true));
    this.hideOptions = [];
    this.orgId = 'error';
    this.env = 'prod';
    this.mediaId = '';
    this.stream = '';
    this.meta = {
      ready: false,
      title: '',
      sampleRate: 0,
      live: false,
      captions: false,
    };
    this.arcToken = '';
    this.loadingContainer = undefined;
    this.frameStyleOptions = undefined;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'class' && oldValue !== newValue) {
      this.syncRegisteredControllerClasses();
    }
  }

  protected registerMediaController(controller: HTMLElement): void {
    this._registeredMediaController = controller;
    this.syncRegisteredControllerClasses();
  }

  protected unregisterMediaController(controller: HTMLElement): void {
    if (this._registeredMediaController === controller) {
      this._registeredMediaController = undefined;
    }
  }

  private syncRegisteredControllerClasses(): void {
    const controller = this._registeredMediaController;
    if (!controller) {
      return;
    }

    const previous = controller.getAttribute('data-host-classes');
    if (previous) {
      previous
        .split(/\s+/)
        .filter(Boolean)
        .forEach(cls => controller.classList.remove(cls));
    }

    const hostClasses = Array.from(this.classList).filter(Boolean);
    if (hostClasses.length) {
      controller.classList.add(...hostClasses);
      controller.setAttribute('data-host-classes', hostClasses.join(' '));
    } else {
      controller.removeAttribute('data-host-classes');
    }
  }

  protected showLoadingState(message?: string, options: LoadingStateOptions = {}): void {
    const resolvedMessage = message ?? this.t('loading.media');
    this.storeFrameStyle(options);
    if (this.loadingContainer) {
      const label = this.loadingContainer.querySelector('.koi-loading-label');
      if (label) {
        label.textContent = resolvedMessage;
      }
      this.applyFrameStyle(this.loadingContainer, options);
      return;
    }

    const container = document.createElement('div');
    container.className = 'koi-loading-state';
    container.setAttribute('part', 'loading');
    container.setAttribute('role', 'status');
    container.setAttribute('aria-live', 'polite');

    const spinner = document.createElement('div');
    spinner.className = 'koi-loading-spinner';
    spinner.setAttribute('part', 'loading-icon');
    spinner.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'koi-loading-label';
    label.setAttribute('part', 'loading-message');
    label.textContent = resolvedMessage;

    const row = document.createElement('div');
    row.className = 'koi-loading-row';
    row.setAttribute('part', 'loading-row');
    row.appendChild(spinner);
    row.appendChild(label);

    container.appendChild(row);
    this.applyFrameStyle(container, options);

    this.shadowRoot.appendChild(container);
    this.loadingContainer = container;
  }

  protected hideLoadingState(): void {
    if (this.loadingContainer) {
      this.loadingContainer.remove();
      this.loadingContainer = undefined;
    }
  }

  private storeFrameStyle(options?: LoadingStateOptions): void {
    if (!options) {
      return;
    }
    const hasMinHeight = typeof options.minHeight === 'number';
    const hasAspectRatio =
      typeof options.aspectRatio === 'string' && options.aspectRatio.trim().length > 0;
    if (hasMinHeight || hasAspectRatio) {
      this.frameStyleOptions = { ...options };
    }
  }

  private applyFrameStyle(target: HTMLElement, override?: LoadingStateOptions): void {
    const source =
      override && (override.minHeight !== undefined || override.aspectRatio !== undefined)
        ? override
        : this.frameStyleOptions;

    if (source?.minHeight !== undefined) {
      target.style.minHeight = `${source.minHeight}px`;
    } else {
      target.style.removeProperty('min-height');
    }

    if (source?.aspectRatio) {
      target.style.aspectRatio = source.aspectRatio;
    } else {
      target.style.removeProperty('aspect-ratio');
    }
  }

  private getBaseAudioUrl(env: string, orgId: string, mediaId: string) {
    return `https://${orgId}-config-${env}.api.arc-cdn.net/audio/v1/media/clip/${mediaId}`;
  }

  shouldHideElement(elementType: string): boolean {
    if (!this.hideOptions || !this.hideOptions.length) return false;
    return this.hideOptions.includes(elementType);
  }
  /**
   * Renders an error message in the shadow DOM
   *
   * @param message - The error message to display
   */
  renderError(message: string): void {
    this.hideLoadingState();
    if (this._registeredMediaController) {
      this.unregisterMediaController(this._registeredMediaController);
      this._registeredMediaController.remove();
    }
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }

    const errorContainer = document.createElement('div');
    errorContainer.setAttribute('part', 'error');
    errorContainer.setAttribute(
      'style',
      `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 24px;
      border-radius: 12px;
      background: var(--koi-error-background, #FDF1D5);
      color: var(--koi-error-text, #191B34);
      font-size: 1rem;
      line-height: 1.5;
      min-height: 100px;
    `
    );
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('aria-live', 'assertive');

    const iconWrapper = document.createElement('span');
    iconWrapper.setAttribute('part', 'error-icon');
    iconWrapper.setAttribute(
      'style',
      `
      width: 36px;
      height: 36px;
      border-radius: 50%;
      color: var(--koi-error-icon-color, #692609);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    `
    );
    iconWrapper.setAttribute('aria-hidden', 'true');
    iconWrapper.innerHTML = `
      <svg role="presentation" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_1659_2661)">
          <path d="M15.4126 13.3332L9.18128 1.4332C9.06835 1.21769 8.8986 1.0372 8.69042 0.91126C8.48225 0.785324 8.24358 0.71875 8.00028 0.71875C7.75697 0.71875 7.51831 0.785324 7.31013 0.91126C7.10196 1.0372 6.93221 1.21769 6.81928 1.4332L0.587278 13.3332C0.480826 13.5364 0.428614 13.7637 0.435694 13.993C0.442773 14.2223 0.508903 14.4459 0.627689 14.6422C0.746475 14.8385 0.913898 15.0008 1.11377 15.1134C1.31364 15.2261 1.53919 15.2852 1.76861 15.2852H14.2313C14.4607 15.2852 14.6863 15.2261 14.8861 15.1134C15.086 15.0008 15.2534 14.8385 15.3722 14.6422C15.491 14.4459 15.5571 14.2223 15.5642 13.993C15.5713 13.7637 15.5191 13.5364 15.4126 13.3332ZM7.33328 5.6152C7.33328 5.43839 7.40352 5.26882 7.52854 5.1438C7.65357 5.01877 7.82313 4.94853 7.99995 4.94853C8.17676 4.94853 8.34633 5.01877 8.47135 5.1438C8.59637 5.26882 8.66661 5.43839 8.66661 5.6152V9.6152C8.66661 9.79201 8.59637 9.96158 8.47135 10.0866C8.34633 10.2116 8.17676 10.2819 7.99995 10.2819C7.82313 10.2819 7.65357 10.2116 7.52854 10.0866C7.40352 9.96158 7.33328 9.79201 7.33328 9.6152V5.6152ZM8.03328 13.2885H8.01461C7.75171 13.2877 7.4993 13.1852 7.3102 13.0026C7.12109 12.8199 7.00992 12.5713 6.99995 12.3085C6.99514 12.1779 7.01642 12.0476 7.06255 11.9252C7.10868 11.8029 7.17873 11.691 7.2686 11.596C7.35847 11.501 7.46637 11.4249 7.58599 11.3721C7.70561 11.3193 7.83455 11.2909 7.96528 11.2885H7.98395C8.2468 11.2888 8.49935 11.3908 8.68872 11.5731C8.87809 11.7554 8.98962 12.0039 8.99995 12.2665C9.00512 12.3975 8.98406 12.5282 8.938 12.6509C8.89195 12.7736 8.82183 12.8858 8.73177 12.981C8.64171 13.0763 8.53352 13.1525 8.41357 13.2053C8.29362 13.2581 8.16432 13.2864 8.03328 13.2885Z" fill="var(--koi-error-icon-contrast, #692609)"/>
        </g>
        <defs>
          <clipPath id="clip0_1659_2661">
            <rect width="16" height="16" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    `;

    const errorMessage = document.createElement('p');
    errorMessage.setAttribute('part', 'error-message');
    errorMessage.setAttribute('style', 'margin: 0; font-weight: 500;');
    errorMessage.textContent = message;

    errorContainer.appendChild(iconWrapper);
    errorContainer.appendChild(errorMessage);
    this.shadowRoot.appendChild(errorContainer);

    // Mark player as not ready and broadcast failure so hosts can respond.
    this.meta.ready = false;
    this.finalizeSetup(false);
  }

  /**
   * Fetches video data from the Video API
   *
   * @param mediaId - The video ID
   * @param orgId - The organization ID
   * @param env - The environment (e.g., 'prod', 'dev', 'staging')
   * @returns A promise that resolves to the video data
   * @throws Error if there's an error fetching the video data
   */
  async fetchVideoData(mediaId: string, orgId: string, env: string = 'prod'): Promise<MediaData> {
    try {
      const apiUrl = `https://${orgId}-${env}-cdn.video-api.arcpublishing.com/api/v1/ansvideos/findByUuid?uuid=${mediaId}`;

      const response = await fetch(apiUrl, {
        mode: 'cors',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer-when-downgrade',
      });

      if (!response || !response.ok) {
        const details = response
          ? `${response.status} ${response.statusText}`
          : this.t('errors.noResponseReceived');
        return Promise.reject(new Error(this.t('errors.failedToFetchVideo', { details })));
      }

      const data = await response.json();

      const videoData: VideoData = Array.isArray(data) && data.length > 0 ? data[0] : data;

      const mediaUrl =
        videoData.streams && videoData.streams.length > 0 ? videoData.streams[0].url : null;

      const thumbnailUrl =
        videoData.promo_items && videoData.promo_items.basic
          ? videoData.promo_items.basic.url
          : undefined;

      this.meta.title = videoData?.headlines?.basic
        ? videoData.headlines.basic
        : this.t('titles.defaultVideo');

      if (videoData.powaConfig && videoData.powaConfig.sampleRate !== undefined) {
        this.meta.sampleRate = videoData.powaConfig.sampleRate;
      } else {
        this.meta.sampleRate = defaultSampleRate;
      }

      //todo this is a mess
      const webVttTracks: CaptionTrackData[] = [];
      if (videoData.subtitles && videoData.subtitles.urls && videoData.subtitles.urls.length > 0) {
        // Video API only supports a single caption track right now.
        const rawTrack = videoData.subtitles.urls.find(track => track!.format === 'WEB_VTT'); // todo
        if (rawTrack) {
          webVttTracks.push({
            label: this.t('labels.english'),
            url: rawTrack.url,
            lang: 'en',
          });
        }
      }

      if (!mediaUrl) {
        return Promise.reject(new Error(this.t('errors.noMediaUrl')));
      }

      return {
        mediaUrl,
        thumbnailUrl: thumbnailUrl,
        title: videoData.headlines?.basic || this.t('titles.defaultVideo'),
        captionTracks: webVttTracks,
      };
    } catch (error) {
      console.error('KoiPlayer: Error fetching video data', error);
      const errorMessage = this.t('errors.errorLoadingVideoWithReason', {
        reason: (error as Error).message,
      });
      return Promise.reject(new Error(errorMessage));
    }
  }

  async fetchAudioData({
    mediaId,
    orgId,
    env = 'prod',
    renditionKey = 'aac-standard',
    showWaveform = false,
  }: {
    mediaId?: string;
    orgId?: string;
    env?: string;
    renditionKey?: string;
    showWaveform?: boolean;
  }): Promise<MediaData> {
    try {
      if (!mediaId || !orgId) {
        return Promise.reject(new Error(this.t('errors.mediaIdOrgIdRequired')));
      }

      const baseUrl =
        this.arcToken !== ''
          ? this.getBaseAudioUrl(env, orgId, mediaId)
          : `/audiocenter/api/editorial/v1/clips/${mediaId}/binary`;

      const audioFetchUrl = `${baseUrl}?renditionKey=${renditionKey}${showWaveform ? '&waveform=true' : ''}`;

      const response = await fetch(audioFetchUrl, {
        mode: 'cors',
        credentials: 'same-origin',
        referrerPolicy: 'no-referrer-when-downgrade',
        headers: {
          'x-api-key': this.arcToken || '',
        },
      });

      if (!response || !response.ok) {
        const details = response
          ? `${response.status} ${response.statusText}`
          : this.t('errors.noResponseReceived');
        return Promise.reject(new Error(this.t('errors.failedToFetchAudio', { details })));
      }

      const data: Deliverable = await response.json();
      if (!data.uri) {
        return Promise.reject(new Error(this.t('errors.noUriFound')));
      }
      if (!data.waveform) {
        return { mediaUrl: data.uri };
      } else {
        return { mediaUrl: data.uri, waveform: data.waveform };
      }
    } catch (error) {
      console.error('KoiPlayer: Error fetching audio data', error);
      const errorMessage = this.t('errors.errorLoadingAudioWithReason', {
        reason: (error as Error).message,
      });
      return Promise.reject(new Error(errorMessage));
    }
  }

  generateControlElements(type: MediaType, live: boolean, captions: boolean): DocumentFragment {
    const fragment = document.createDocumentFragment();

    const controls = PlayerUiOptions.getDefaultControls(type, live, captions);

    controls.forEach(control => {
      if (this.shouldHideElement(control.name)) {
        return;
      }

      const element = document.createElement(control.elementName);

      Object.entries(control.attributes).forEach(([key, value]) => {
        // If the attribute value is a function, call it to get the actual value
        const attrValue = typeof value === 'function' ? value() : value;
        element.setAttribute(key, attrValue);
      });

      fragment.appendChild(element);
    });

    return fragment;
  }

  finalizeSetup(ready: boolean): void {
    if (typeof document.dispatchEvent !== 'function') {
      return;
    }

    const eventName = ready ? 'koi-player-ready' : 'koi-player-error';
    const detail = ready
      ? { player: this }
      : { player: this, error: this.t('errors.failedToInitialize') };

    // Defer dispatch so host frameworks (React, etc.) have a chance to attach listeners.
    setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail,
        })
      );
    }, 0);
  }

  connectedCallback(): void {
    // Accessibility
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

    this.stream = this.getAttribute('stream') || '';
    this.mediaId = this.getAttribute('media-id') || '';
    this.showWaveform = !!this.getAttribute('show-waveform') || false;

    this.arcToken = this.getAttribute('arc-token') || '';
    if (this.hasAttribute('arc-token')) {
      this.removeAttribute('arc-token');
    }

    this.hideOptions = this.getAttribute('hide') ? this.getAttribute('hide')!.split(',') : [];
    if (this.hasAttribute('config')) {
      try {
        const configJson = this.getAttribute('config') || '{}';
        const config = JSON.parse(configJson) as KoiConfig;
        // config takes precedence
        if (config.type) this.type = config.type as MediaType;
        if (config.waveform !== undefined) this.showWaveform = Boolean(config.waveform);
        if (config.live) this.meta.live = Boolean(config.live);
        if (config.mediaId) this.mediaId = config.mediaId;
        if (config.stream) this.stream = config.stream;
        if (config.orgId) this.orgId = config.orgId;
        if (config.env) this.env = config.env;
        if (config.hideOptions)
          this.hideOptions = Array.isArray(config.hideOptions) ? config.hideOptions : [];
        if (config.arcToken) {
          this.arcToken = config.arcToken;
          // Remove arcToken from config and update the config attribute
          delete config.arcToken;
          this.setAttribute('config', JSON.stringify(config));
        }
      } catch (error) {
        console.error('KoiPlayer: Error parsing config attribute:', error);
      }
    }
  }
}

export default BasePlayer;
